import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { types as sdkTypes } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import { denormalisedResponseEntities } from '../../util/data';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { isUserAuthorized, hasPermissionToInitiateTransactions } from '../../util/userHelpers';
import {
  inquiryParams,
  queryTransactionMessages,
  messagesFromResponse,
  sendTransactionMessage,
  saveMessageAttachment,
} from '../../util/transactionMessages';

const { UUID } = sdkTypes;
const include = [
  'listing',
  'provider',
  'provider.profileImage',
  'customer',
  'customer.profileImage',
];
const imageFields = { 'fields.image': ['variants.square-small', 'variants.square-small2x'] };
const ref = entity => ({ id: entity.id, type: entity.type });
const merge = (a, b) => a.filter(x => !b.some(y => y.id.uuid === x.id.uuid)).concat(b);

export const fetchConversations = createAsyncThunk(
  'MessagesPage/fetchConversations',
  async ({ page = 1 } = {}, { dispatch, rejectWithValue, extra: sdk }) => {
    try {
      const response = await sdk.transactions.query({
        include: [...include, 'messages', 'messages.sender'],
        'limit.messages': 1,
        ...imageFields,
        sort: 'lastMessageAt',
        page,
        perPage: 25,
      });
      dispatch(addMarketplaceEntities(response));
      return {
        refs: response.data.data.map(ref),
        pagination: response.data.meta,
        fetchedAt: Date.now(),
      };
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  },
  { condition: (_, { getState }) => !getState().MessagesPage.fetchInProgress }
);

export const fetchThread = createAsyncThunk(
  'MessagesPage/fetchThread',
  async ({ id, page = 1 }, { dispatch, rejectWithValue, extra: sdk }) => {
    try {
      const txId = new UUID(id);
      const [txResponse, messageResponse] = await Promise.all([
        page === 1 ? sdk.transactions.show({ id: txId, include, ...imageFields }) : null,
        queryTransactionMessages(sdk, { txId, page }),
      ]);
      if (txResponse) dispatch(addMarketplaceEntities(txResponse));
      return {
        ...messagesFromResponse(messageResponse),
        transactionRef: txResponse ? ref(txResponse.data.data) : null,
      };
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  },
  { condition: ({ id }, { getState }) => !getState().MessagesPage.threads[id]?.fetchInProgress }
);

export const sendConversationMessage = createAsyncThunk(
  'MessagesPage/sendMessage',
  async ({ id, message, attachment }, { dispatch, getState, rejectWithValue, extra: sdk }) => {
    try {
      const result = await sendTransactionMessage({
        sdk,
        txId: new UUID(id),
        message,
        attachment,
        currentUser: getState().user.currentUser,
        onUploadProgress: event =>
          dispatch(
            uploadProgressChanged({
              id,
              progress: event.total
                ? Math.min(100, Math.round((event.loaded * 100) / event.total))
                : null,
            })
          ),
        onSent: message => dispatch(messageReceived({ id, message })),
      });
      if (result.metadataResponse?.data?.data)
        dispatch(addMarketplaceEntities(result.metadataResponse));
      // Retain only a failed metadata write for retry; the message is already delivered.
      return {
        pendingAttachment: result.attachmentSaveError
          ? {
              messageId: result.message.id,
              uploadedAttachment: result.uploadedAttachment,
            }
          : null,
      };
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  },
  { condition: ({ id }, { getState }) => !getState().MessagesPage.threads[id]?.sendInProgress }
);

export const retryAttachment = createAsyncThunk(
  'MessagesPage/retryAttachment',
  async ({ id }, { dispatch, getState, rejectWithValue }) => {
    const pending = getState().MessagesPage.threads[id]?.pendingAttachment;
    if (!pending) return;
    try {
      const response = await saveMessageAttachment(
        new UUID(id),
        pending.messageId,
        pending.uploadedAttachment
      );
      if (response?.data?.data) dispatch(addMarketplaceEntities(response));
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

export const canContactListing = (listing, currentUser) => {
  if (
    !listing?.author?.id ||
    listing.author.id.uuid === currentUser?.id?.uuid ||
    listing.attributes?.deleted ||
    listing.attributes?.state !== 'published' ||
    listing.author.attributes?.deleted ||
    listing.author.attributes?.banned
  )
    return false;
  try {
    inquiryParams(listing);
    return true;
  } catch (_) {
    return false;
  }
};

export const searchConversationListings = createAsyncThunk(
  'MessagesPage/searchListings',
  async ({ keywords, page = 1 }, { getState, rejectWithValue, extra: sdk }) => {
    try {
      const response = await sdk.listings.query({
        keywords: keywords.trim(),
        page,
        perPage: 20,
        include: ['author', 'author.profileImage'],
        ...imageFields,
      });
      return {
        listings: denormalisedResponseEntities(response).filter(l =>
          canContactListing(l, getState().user.currentUser)
        ),
        pagination: response.data.meta,
      };
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

export const startConversation = createAsyncThunk(
  'MessagesPage/startConversation',
  async ({ listing, message, attachment }, { dispatch, getState, rejectWithValue, extra: sdk }) => {
    try {
      const currentUser = getState().user.currentUser;
      if (
        !isUserAuthorized(currentUser) ||
        !hasPermissionToInitiateTransactions(currentUser) ||
        !canContactListing(listing, currentUser)
      )
        throw new Error('Contact is not permitted.');
      if (!message?.trim() && !attachment) throw new Error('Message or attachment is required.');
      let id = getState().MessagesPage.draftTransactions[listing.id.uuid];
      if (!id) {
        // Reuse the conversation for this pair and listing. No purchase/booking transition occurs.
        const existing = await sdk.transactions.query({
          listingId: listing.id,
          userId: listing.author.id,
          sort: 'lastMessageAt',
          perPage: 1,
        });
        const transaction =
          existing.data.data[0] ||
          (await sdk.transactions.initiate(inquiryParams(listing, message))).data.data;
        id = transaction.id.uuid;
        dispatch(draftTransactionCreated({ listingId: listing.id.uuid, id }));
      }
      await dispatch(sendConversationMessage({ id, message, attachment })).unwrap();
      // Navigation will load the new transaction and merge it into the inbox.
      return id;
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  },
  { condition: (_, { getState }) => !getState().MessagesPage.startInProgress }
);

const initialState = {
  transactionRefs: [],
  pagination: null,
  fetchedAt: null,
  fetchInProgress: false,
  fetchError: null,
  threads: {},
  listings: [],
  searchInProgress: false,
  searchError: null,
  searchPagination: null,
  startInProgress: false,
  startError: null,
  draftTransactions: {},
};
const threadFor = (state, id) =>
  state.threads[id] ||
  (state.threads[id] = {
    messages: [],
    page: 0,
    totalPages: 0,
    fetchInProgress: false,
    sendInProgress: false,
  });
const slice = createSlice({
  name: 'MessagesPage',
  initialState,
  reducers: {
    uploadProgressChanged: (state, { payload }) => {
      threadFor(state, payload.id).uploadProgress = payload.progress;
    },
    messageReceived: (state, { payload: { id, message } }) => {
      const thread = threadFor(state, id);
      thread.messages = merge(thread.messages, [message]);
      thread.latestMessage = message;
    },
    draftTransactionCreated: (state, { payload }) => {
      state.draftTransactions[payload.listingId] = payload.id;
    },
    resetSearch: state => {
      state.listings = [];
      state.searchPagination = null;
      state.searchError = null;
      state.startError = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchConversations.pending, state => {
        state.fetchInProgress = true;
        state.fetchError = null;
      })
      .addCase(fetchConversations.fulfilled, (state, { payload, meta }) => {
        state.fetchInProgress = false;
        state.transactionRefs =
          meta.arg?.page > 1 ? merge(state.transactionRefs, payload.refs) : payload.refs;
        state.pagination = payload.pagination;
        state.fetchedAt = payload.fetchedAt;
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.fetchInProgress = false;
        state.fetchError = action.payload || action.error;
      })
      .addCase(fetchThread.pending, (state, { meta }) => {
        const t = threadFor(state, meta.arg.id);
        t.fetchInProgress = true;
        t.fetchError = null;
      })
      .addCase(fetchThread.fulfilled, (state, { payload, meta }) => {
        const t = threadFor(state, meta.arg.id);
        t.fetchInProgress = false;
        t.loaded = true;
        t.messages = merge(t.messages, payload.messages);
        t.page = Math.max(t.page, payload.pagination.page);
        t.totalPages = payload.pagination.totalPages;
        t.latestMessage = [...t.messages].sort(
          (a, b) => new Date(b.attributes.createdAt) - new Date(a.attributes.createdAt)
        )[0];
        if (payload.transactionRef && !state.transactionRefs.some(r => r.id.uuid === meta.arg.id)) {
          state.transactionRefs.unshift(payload.transactionRef);
        }
      })
      .addCase(fetchThread.rejected, (state, { payload, error, meta }) => {
        const t = threadFor(state, meta.arg.id);
        t.fetchInProgress = false;
        t.fetchError = payload || error;
      })
      .addCase(sendConversationMessage.pending, (state, { meta }) => {
        const t = threadFor(state, meta.arg.id);
        t.sendInProgress = true;
        t.sendError = null;
        t.uploadProgress = null;
      })
      .addCase(sendConversationMessage.fulfilled, (state, { payload, meta }) => {
        const t = threadFor(state, meta.arg.id);
        t.sendInProgress = false;
        t.uploadProgress = null;
        t.pendingAttachment = payload.pendingAttachment;
      })
      .addCase(sendConversationMessage.rejected, (state, { payload, error, meta }) => {
        const t = threadFor(state, meta.arg.id);
        t.sendInProgress = false;
        t.uploadProgress = null;
        t.sendError = payload || error;
      })
      .addCase(retryAttachment.pending, (state, { meta }) => {
        threadFor(state, meta.arg.id).retryInProgress = true;
      })
      .addCase(retryAttachment.fulfilled, (state, { meta }) => {
        const t = threadFor(state, meta.arg.id);
        t.pendingAttachment = null;
        t.retryInProgress = false;
      })
      .addCase(retryAttachment.rejected, (state, { meta }) => {
        threadFor(state, meta.arg.id).retryInProgress = false;
      })
      .addCase(searchConversationListings.pending, (state, { meta }) => {
        state.searchInProgress = true;
        state.searchError = null;
        state.searchRequestId = meta.requestId;
      })
      .addCase(searchConversationListings.fulfilled, (state, { payload, meta }) => {
        if (state.searchRequestId !== meta.requestId) return;
        state.searchInProgress = false;
        state.listings = payload.listings;
        state.searchPagination = payload.pagination;
      })
      .addCase(searchConversationListings.rejected, (state, { payload, error, meta }) => {
        if (state.searchRequestId !== meta.requestId) return;
        state.searchInProgress = false;
        state.searchError = payload || error;
      })
      .addCase(startConversation.pending, state => {
        state.startInProgress = true;
        state.startError = null;
      })
      .addCase(startConversation.fulfilled, state => {
        state.startInProgress = false;
      })
      .addCase(startConversation.rejected, (state, action) => {
        state.startInProgress = false;
        state.startError = action.payload || action.error;
      });
  },
});
export const {
  uploadProgressChanged,
  messageReceived,
  draftTransactionCreated,
  resetSearch,
} = slice.actions;
export default slice.reducer;

export const loadData = () => (dispatch, getState) =>
  Promise.all([
    getState().user.currentUser
      ? Promise.resolve()
      : dispatch(fetchCurrentUser({ updateHasListings: false, updateNotifications: false })),
    !getState().MessagesPage.fetchedAt || Date.now() - getState().MessagesPage.fetchedAt > 60000
      ? dispatch(fetchConversations())
      : Promise.resolve(),
  ]);
